/**
 * The M2 HUD model (plan §6 M2-19; spec §4, §5.2-§5.4, §6.3, §7.2): everything the career HUD shows, computed per
 * frame from the M2 runtime (readouts, the mirror, pools, stations, the space event ring), the career snapshot and
 * the live HUD camera into one preallocated `M2HudState`. Pure of the DOM (view.ts turns it into element writes).
 *
 * Float64 world maths throughout (D-011): every marker goes through `projectWorld`, which subtracts the camera in
 * float64 first. Allocation-free per frame (D-014). Strings are built only on events (a toast, a completed scan),
 * never per frame; the view formats numbers only when their quantised value changes.
 */
import type { Career } from '../../career/types'
import type { Vec3 } from '../../core/math'
import { loadGalaxyConfig } from '../../data/galaxyConfig'
import { CONTRACT_TYPES, MAX_ANCHORS_PER_PART, MAX_PARTS_PER_WRECK, TETHERSTONE_INDEX, WARE_COUNT, type M2Data } from '../../data/m2Schemas'
import type { CareerNoticeName, M2HudTuning, ToastKind } from '../../features/m2hud/tuning'
import { CONDITION_BAND } from '../../salvage/generate'
import type { TravelConfig } from '../../travel/config'
import { createCruiseTarget, cruiseEtaS, targetDropM } from '../../travel/runtime/kinematics'
import hulksJson from '../../data/hulks.json'
import {
  COLLECTIONS_CODE,
  MINING_MODE,
  NAV,
  SALVAGE_STATUS,
  TOOL,
  WRECK_KIND,
  type CareerMirror,
  type DockingRuntime,
  type PlayerToolInput,
  type PlayerToolState,
  type RocksRuntime,
  type SalvageRuntime,
  type StationsRuntime,
  type ToolsRuntime,
} from '../../space/contracts'
import { CAREER_NOTICE, CLEARANCE_REASON, ENCOUNTER_KIND, HOLD_FULL_SOURCE, SEV, firstReadableSpace, spaceEventSlot, type SpaceEventRing } from '../../space/events'
import { createScreenPoint, projectDirection, projectWorld, type HudCamera, type ScreenPoint } from '../projection'
import { loadReachHudConfig } from '../reach/config'
import { formatHudDistance } from '../reach/format'
import { keyGlyph } from '../reach/labels'
import { createEdgeMarkerLayout, layoutEdgeMarkers, reserveEdgeArrow, resetEdgeReserved, type EdgeMarkerLayout } from '../reach/edgeMarkers'

/** Anchor markers in the pool: every anchor of the largest wreck. */
export const ANCHOR_MARKERS = MAX_PARTS_PER_WRECK * MAX_ANCHORS_PER_PART
/** Part brackets in the pool: every part of the largest wreck. */
export const PART_BRACKETS = MAX_PARTS_PER_WRECK
/** Scan panel rows: one per ware (rock) or per part (wreck). */
export const SCAN_ROWS = WARE_COUNT > MAX_PARTS_PER_WRECK ? WARE_COUNT : MAX_PARTS_PER_WRECK

/** `M2HudDock.phase` codes (DockingPhase order). */
export const DOCK_PHASE = { space: 0, cleared: 1, autodock: 2, docked: 3 } as const
/** Station marker states. */
export const STATION_STATE = { none: 0, cleared: 1, autodock: 2, docked: 3 } as const
/** `M2HudDock.prompt` (CL-2): the next step after clearance. `inRange` = press dock to auto-dock; `far` = close in first. */
export const DOCK_PROMPT = { none: 0, inRange: 1, far: 2 } as const
/** Scan panel kinds. */
export const SCAN_KIND = { none: 0, rock: 1, wreck: 2 } as const

/** The slice of the space world the HUD reads (a `SpaceWorld` satisfies it). */
export interface M2HudSpace {
  readonly data: Pick<M2Data, 'wares' | 'docking' | 'modules'>
  readonly events: SpaceEventRing
  readonly player: PlayerToolState
  readonly mirror: CareerMirror
  readonly tools: Pick<ToolsRuntime, 'scan'>
  readonly rocks: Pick<RocksRuntime, 'readout' | 'position' | 'radius'>
  readonly salvage: Pick<SalvageRuntime, 'readout' | 'wrecks' | 'parts'>
  readonly docking: Pick<DockingRuntime, 'readout'>
  readonly stations: Pick<StationsRuntime, 'list'>
  /** The player's tool input: `flightActive` names the cause of an `autodockCancel` (CL-2). Read only. */
  readonly input: Pick<PlayerToolInput, 'flightActive'>
}

/** What the model reads each frame. The feature refills one instance in place. */
export interface M2HudSource {
  space: M2HudSpace
  /** The career snapshot (null before a career exists). */
  career: Career | null
  /** A UI screen (station, title, death, ownership) is up: the whole HUD hides. */
  uiOpen: boolean
  playerEid: number
  /** The player exists and is alive. */
  alive: boolean
  /** Player position, float64 world. */
  px: number
  py: number
  pz: number
  cam: HudCamera
  /** The combat target (float64 world) while one is selected: its edge arrow keeps the station labels clear. */
  targetOn: boolean
  tx: number
  ty: number
  tz: number
  /** Real seconds since the last frame (toasts age by it unless paused). */
  dt: number
  paused: boolean
  /** The keyboard code bound to `dock` (bindings.json, "KeyF"); its glyph goes in the dock prompt (CL-2). */
  dockKey: string
  /** The pad is the active device: the dock prompt shows `labels.dockPadGlyph` instead of the key. */
  padActive: boolean
  /** The travel tuning (reach-travel.json) for the far markers' cruise times (CL-8); null = no cruise times. */
  travel: TravelConfig | null
  /** The flown hull's drive charge plus its spin-up lag (s, `cruiseChargeSFor` + `cruiseLagSFor`): the cruise lead-in. */
  cruiseLeadS: number
}

export interface M2Marker {
  on: boolean
  point: ScreenPoint
  /** Metres from the player. */
  distance: number
  /** Seconds of cruise to it (CL-8, `stationCruiseS`); 0 = none (within mass-lock or drop range, or not a station). */
  cruiseS: number
  /** Laid-out position (edge-pinned inside the HUD safe rect, spread and decluttered; reach/edgeMarkers.ts). */
  sx: number
  sy: number
  /** Pinned to the safe rect's edge, label flipped left of the icon, label text shown. */
  edge: boolean
  flip: boolean
  full: boolean
}

export interface M2StationMarker extends M2Marker {
  /** Station name (the def's string; never built per frame). */
  name: string
  /** `STATION_STATE`. */
  state: number
  /** The destination of an active delivery or courier contract. */
  contract: boolean
}

export interface M2ContractMarker extends M2Marker {
  /** Index into `CONTRACT_TYPES`. */
  type: number
}

export interface M2HudNav {
  /** `NAV` kind; `NAV.none` = no target. */
  kind: number
  point: ScreenPoint
  halfPx: number
  distance: number
  /** Seconds of cruise to a station nav target (CL-8, `stationCruiseS`); 0 = none. */
  cruiseS: number
}

export interface M2HudScan {
  /** A scan is in progress (0 < progress < 1). */
  scanning: boolean
  progress: number
  /** `SCAN_KIND` of the result panel; none = hidden. */
  kind: number
  title: string
  value: string
  mode: string
  tier: string
  /** Rock scans: the field grade and the system's signature ore (MC-a6); empty outside every field and for wrecks. */
  field: string
  rowCount: number
  rowLabel: string[]
  rowValue: string[]
  /** 1 = coolant anchor (wreck rows) or unidentified tetherstone (rock rows). */
  rowFlag: Uint8Array
  /** The result the strings were built for. */
  builtKind: number
  builtIndex: number
  /** A `scanComplete` event arrived since the strings were built. */
  dirty: boolean
}

export interface M2HudMining {
  /** Mining mode. */
  on: boolean
  beamOn: boolean
  contact: boolean
  /** `MINING_MODE` of the rock under the beam. */
  mode: number
  /** The fracture bar is up (a fracture is engaged, or the laser cannot crack the rock). */
  fracture: boolean
  power: number
  /** The fitted laser's max power: the Mk line. */
  maxPower: number
  /** Stability window on the 0..1 bar, clamped to it. */
  windowLeft: number
  windowWidth: number
  /** -1 below the window, 0 inside, 1 above. */
  band: number
  instability: number
  progress: number
  tooHard: boolean
  heat: number
  overheated: boolean
  cooldownLeft: number
}

export interface M2HudSalvage {
  /** Salvage mode. */
  on: boolean
  /** Wreck whose parts and anchors are marked, or -1. */
  wreck: number
  /** The focus wreck has a completed scan (values and coolant flags known). */
  scanned: boolean
  anchorCount: number
  anchorX: Float32Array
  anchorY: Float32Array
  /** 0..1 cut progress. */
  anchorCut: Float32Array
  /** 1 = a (scanned) coolant anchor: marked by shape and colour. */
  anchorCoolant: Uint8Array
  /** 1 = the anchor under the beam. */
  anchorActive: Uint8Array
  partCount: number
  partX: Float32Array
  partY: Float32Array
  partHalf: Float32Array
  partValueLow: Float32Array
  partValueHigh: Float32Array
  /** 1 = value known (from the scan). */
  partKnown: Uint8Array
  partCondition: Float32Array
  /** 1 = freed (a physics body); 2 = held by the tractor. */
  partState: Uint8Array
  beamOn: boolean
  cutProgress: number
  spilling: boolean
  tractorOn: boolean
  /** `SALVAGE_STATUS`. */
  status: number
}

export interface M2HudDock {
  /** The docking panel is up (cleared, auto-docking, or in a corridor). */
  on: boolean
  /** `DOCK_PHASE`. */
  phase: number
  station: number
  clearanceLeft: number
  speed: number
  speedLimit: number
  /** Speed past the limit that applies here. */
  overLimit: boolean
  inCorridor: boolean
  inBay: boolean
  /** Guides (reticle, nose cue, pad target) are up: in the corridor, in the bay or auto-docking. */
  guides: boolean
  padOn: boolean
  pad: ScreenPoint
  noseOn: boolean
  nose: ScreenPoint
  alignDeg: number
  rollDeg: number
  /** Nose and roll within the dock limit. */
  aligned: boolean
  padDistance: number
  holdProgress: number
  /** `DOCK_PROMPT` (CL-2): shown while cleared, by the hull distance against `docking.autodockRange`. */
  prompt: number
  /** The prompt line (rebuilt only when the key glyph, the range or a template changes; compared by identity). */
  promptText: string
}

/** The dock prompt's string cache (CL-2): each line is rebuilt only when one of its inputs changes. */
export interface M2DockPromptCache {
  keyCode: string
  pad: boolean
  keyTemplate: string
  padGlyph: string
  keyText: string
  range: number
  farTemplate: string
  farText: string
}

export interface M2Toast {
  active: boolean
  /** Bumped whenever the slot takes a new toast (the view rewrites its text then). */
  seq: number
  kind: ToastKind
  speaker: string
  text: string
  age: number
  opacity: number
}

export interface M2HudState {
  /** A career exists and no UI screen is up. */
  visible: boolean
  uiOpen: boolean
  alive: boolean
  /** `TOOL` mode. */
  mode: number
  cargoUsedT: number
  cargoCapacityT: number
  cargoFull: boolean
  wallet: number
  /** Game seconds to the next lease payment (0 when owned). */
  leaseLeft: number
  leaseWarn: boolean
  owned: boolean
  /** `COLLECTIONS_CODE`. */
  collections: number
  nav: M2HudNav
  stations: M2StationMarker[]
  contracts: M2ContractMarker[]
  contractCount: number
  scan: M2HudScan
  mining: M2HudMining
  salvage: M2HudSalvage
  dock: M2HudDock
  promptCache: M2DockPromptCache
  toasts: M2Toast[]
  toastSeq: number
  /** Space-ring cursor and the ring it belongs to. */
  cursor: number
  ring: SpaceEventRing | null
  /** Edge-marker layout scratch: stations first, then contracts. */
  edgeLayout: EdgeMarkerLayout
}

function marker(): M2Marker {
  return { on: false, point: createScreenPoint(), distance: 0, cruiseS: 0, sx: 0, sy: 0, edge: false, flip: false, full: true }
}

export function createM2HudState(stationCount: number, maxContracts: number, maxToasts: number): M2HudState {
  const stations: M2StationMarker[] = []
  for (let i = 0; i < stationCount; i++) stations.push({ ...marker(), name: '', state: STATION_STATE.none, contract: false })
  const contracts: M2ContractMarker[] = []
  for (let i = 0; i < maxContracts; i++) contracts.push({ ...marker(), type: 0 })
  const toasts: M2Toast[] = []
  for (let i = 0; i < maxToasts; i++) toasts.push({ active: false, seq: 0, kind: 'info', speaker: '', text: '', age: 0, opacity: 0 })
  return {
    visible: false,
    uiOpen: false,
    alive: false,
    mode: TOOL.combat,
    cargoUsedT: 0,
    cargoCapacityT: 0,
    cargoFull: false,
    wallet: 0,
    leaseLeft: 0,
    leaseWarn: false,
    owned: false,
    collections: COLLECTIONS_CODE.none,
    nav: { kind: NAV.none, point: createScreenPoint(), halfPx: 0, distance: 0, cruiseS: 0 },
    stations,
    contracts,
    contractCount: 0,
    scan: {
      scanning: false,
      progress: 0,
      kind: SCAN_KIND.none,
      title: '',
      value: '',
      mode: '',
      tier: '',
      field: '',
      rowCount: 0,
      rowLabel: new Array<string>(SCAN_ROWS).fill(''),
      rowValue: new Array<string>(SCAN_ROWS).fill(''),
      rowFlag: new Uint8Array(SCAN_ROWS),
      builtKind: NAV.none,
      builtIndex: -1,
      dirty: false,
    },
    mining: {
      on: false,
      beamOn: false,
      contact: false,
      mode: MINING_MODE.chip,
      fracture: false,
      power: 0,
      maxPower: 0,
      windowLeft: 0,
      windowWidth: 0,
      band: -1,
      instability: 0,
      progress: 0,
      tooHard: false,
      heat: 0,
      overheated: false,
      cooldownLeft: 0,
    },
    salvage: {
      on: false,
      wreck: -1,
      scanned: false,
      anchorCount: 0,
      anchorX: new Float32Array(ANCHOR_MARKERS),
      anchorY: new Float32Array(ANCHOR_MARKERS),
      anchorCut: new Float32Array(ANCHOR_MARKERS),
      anchorCoolant: new Uint8Array(ANCHOR_MARKERS),
      anchorActive: new Uint8Array(ANCHOR_MARKERS),
      partCount: 0,
      partX: new Float32Array(PART_BRACKETS),
      partY: new Float32Array(PART_BRACKETS),
      partHalf: new Float32Array(PART_BRACKETS),
      partValueLow: new Float32Array(PART_BRACKETS),
      partValueHigh: new Float32Array(PART_BRACKETS),
      partKnown: new Uint8Array(PART_BRACKETS),
      partCondition: new Float32Array(PART_BRACKETS),
      partState: new Uint8Array(PART_BRACKETS),
      beamOn: false,
      cutProgress: 0,
      spilling: false,
      tractorOn: false,
      status: SALVAGE_STATUS.ok,
    },
    dock: {
      on: false,
      phase: DOCK_PHASE.space,
      station: -1,
      clearanceLeft: 0,
      speed: 0,
      speedLimit: 0,
      overLimit: false,
      inCorridor: false,
      inBay: false,
      guides: false,
      padOn: false,
      pad: createScreenPoint(),
      noseOn: false,
      nose: createScreenPoint(),
      alignDeg: 0,
      rollDeg: 0,
      aligned: false,
      padDistance: 0,
      holdProgress: 0,
      prompt: DOCK_PROMPT.none,
      promptText: '',
    },
    promptCache: { keyCode: '', pad: false, keyTemplate: '', padGlyph: '', keyText: '', range: -1, farTemplate: '', farText: '' },
    toasts,
    toastSeq: 0,
    cursor: 0,
    ring: null,
    edgeLayout: createEdgeMarkerLayout(stationCount + maxContracts),
  }
}

// ---- Scratch (D-014) ------------------------------------------------------------------------------------------

const navPos: Vec3 = { x: 0, y: 0, z: 0 }
const anchorPoint = createScreenPoint()
const partPoint = createScreenPoint()
const RAD_TO_DEG = 180 / Math.PI

/** Career notice name by code (built once at load). */
const NOTICE_NAMES: (CareerNoticeName | undefined)[] = []
for (const name of Object.keys(CAREER_NOTICE) as CareerNoticeName[]) NOTICE_NAMES[CAREER_NOTICE[name]] = name

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v
}

function clampPx(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v
}

function dist(src: M2HudSource, x: number, y: number, z: number): number {
  const dx = x - src.px
  const dy = y - src.py
  const dz = z - src.pz
  return Math.sqrt(dx * dx + dy * dy + dz * dz)
}

// ---- Formatting (event-driven only: toasts and completed scans) ------------------------------------------------

/** Credits with thousands separators ("12,000"). Locale-stable. */
export function formatCredits(v: number): string {
  const n = Math.round(v)
  const s = String(Math.abs(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return n < 0 ? `-${s}` : s
}

function percent(v: number): number {
  return Math.round(v * 100)
}

function pushToast(state: M2HudState, kind: ToastKind, speaker: string, text: string): void {
  const toasts = state.toasts
  let slot = -1
  let oldest = 0
  for (let i = 0; i < toasts.length; i++) {
    if (!toasts[i].active) {
      slot = i
      break
    }
    if (toasts[i].age > toasts[oldest].age) oldest = i
  }
  if (slot < 0) slot = oldest
  const t = toasts[slot]
  state.toastSeq++
  t.active = true
  t.seq = state.toastSeq
  t.kind = kind
  t.speaker = speaker
  t.text = text
  t.age = 0
  t.opacity = 1
}

function stationSpeaker(src: M2HudSource, station: number, tuning: M2HudTuning): string {
  const list = src.space.stations.list
  if (station < 0 || station >= list.length) return ''
  return list[station].def.name + tuning.speakers.stationSuffix
}

function careerToast(state: M2HudState, code: number, amount: number, tuning: M2HudTuning): void {
  const name = NOTICE_NAMES[code]
  if (name === undefined) return
  const m = tuning.messages.career[name]
  const speaker = m.speaker === 'finance' ? tuning.speakers.finance : ''
  pushToast(state, m.kind, speaker, m.text.replace('{amount}', formatCredits(amount)))
}

/** The `autodockCancel` toast (CL-2): flight input cancelled it, the clearance ran out this frame, or anything else. */
function autodockCancelText(src: M2HudSource, expired: boolean, tuning: M2HudTuning): string {
  const m = tuning.messages.autodockCancelled
  if (src.space.input.flightActive) return m.stick
  return expired ? m.expired : m.other
}

/** The in-range prompt line ("F AUTO-DOCK"). Event-driven: built only when the binding, device or template changes. */
function dockKeyText(c: M2DockPromptCache): string {
  return c.keyTemplate.replace('{key}', c.pad ? c.padGlyph : keyGlyph(c.keyCode))
}

/** The far prompt line ("CLOSE TO 2.0 km TO AUTO-DOCK"). Built only when the range or the template changes. */
function dockFarText(c: M2DockPromptCache): string {
  return c.farTemplate.replace('{range}', formatHudDistance(c.range))
}

function clearanceDeniedText(reason: number, tuning: M2HudTuning): string {
  const d = tuning.messages.clearanceDenied
  if (reason === CLEARANCE_REASON.tooFar) return d.tooFar
  if (reason === CLEARANCE_REASON.notReady) return d.notReady
  if (reason === CLEARANCE_REASON.hostileLock) return d.hostileLock
  if (reason === CLEARANCE_REASON.tooLarge) return d.tooLarge
  return d.none
}

function groupDefeatedText(kind: number, tuning: M2HudTuning): string {
  const g = tuning.messages.groupDefeated
  if (kind === ENCOUNTER_KIND.repo) return g.repo
  if (kind === ENCOUNTER_KIND.contract) return g.contract
  return g.raiders
}

/** Rebuilds the scan panel strings from the tools runtime's scan readout (on a completed scan only). */
function buildScanText(state: M2HudState, src: M2HudSource, tuning: M2HudTuning): void {
  const scan = state.scan
  const result = src.space.tools.scan
  const player = src.space.player
  scan.builtKind = player.scanned.kind
  scan.builtIndex = player.scanned.index
  scan.dirty = false
  scan.rowCount = 0
  const kind = player.scanned.kind
  if ((kind === NAV.rock || kind === NAV.fragment) && result.rock.valid) {
    const r = result.rock
    const wares = src.space.data.wares.wares
    scan.kind = SCAN_KIND.rock
    scan.title = `${kind === NAV.fragment ? 'FRAGMENT' : 'ROCK'} · ${Math.round(r.massT)} t`
    scan.value = r.valueLow === r.valueHigh ? `${formatCredits(r.valueLow)} cr` : `${formatCredits(r.valueLow)}–${formatCredits(r.valueHigh)} cr`
    scan.mode = r.mode === MINING_MODE.fracture ? tuning.labels.fracture : tuning.labels.chip
    scan.tier = `RES T${r.tier + 1} · ${percent(r.resistance)}%`
    scan.field = fieldLine(r.grade, r.signature, src, tuning)
    for (let w = 0; w < WARE_COUNT && scan.rowCount < SCAN_ROWS; w++) {
      if (r.shareHigh[w] <= 0.0005) continue
      const hidden = w === TETHERSTONE_INDEX && !r.tetherstoneIdentified
      const def = wares[w]
      const lo = percent(r.shareLow[w])
      const hi = percent(r.shareHigh[w])
      const k = scan.rowCount++
      scan.rowLabel[k] = hidden && def.unidentifiedName !== undefined ? def.unidentifiedName : def.name
      scan.rowValue[k] = lo === hi ? `${lo}%` : `${lo}–${hi}%`
      scan.rowFlag[k] = hidden ? 1 : 0
    }
    return
  }
  if (kind === NAV.wreck && result.wreck.valid) {
    const wk = result.wreck
    const comps = src.space.data.wares.components
    scan.kind = SCAN_KIND.wreck
    // M9 CH-12a: a hulk has no parts (H-10), only the claim: it reads as a hulk, not an empty wreck.
    const hulk = wk.kind === WRECK_KIND.hulk
    scan.title = hulk ? hulksJson.text.scanTitle : `WRECK · ${wk.partCount} PARTS`
    scan.value = hulk ? '' : wk.totalLow === wk.totalHigh ? `${formatCredits(wk.totalLow)} cr` : `${formatCredits(wk.totalLow)}–${formatCredits(wk.totalHigh)} cr`
    scan.mode = ''
    scan.tier = ''
    scan.field = ''
    const rush = src.space.salvage.parts.rush
    for (let i = 0; i < wk.partCount && scan.rowCount < SCAN_ROWS; i++) {
      const k = scan.rowCount++
      const lo = wk.valueLow[i]
      const hi = wk.valueHigh[i]
      const value = lo === hi ? formatCredits(lo) : `${formatCredits(lo)}–${formatCredits(hi)}`
      const coolant = wk.coolant[i] === 1
      scan.rowLabel[k] = `${comps[wk.type[i]].name} · ${tuning.labels.bands[wk.band[i]]}${installableNote(wk.band[i], wk.partMk[i], tuning)}${rush[wk.part[i]] === 1 ? ` · ${tuning.labels.rushPattern}` : ''}`
      scan.rowValue[k] = `${value} cr · ${percent(wk.condition[i])}%${coolant ? ` · ${tuning.labels.coolant}` : ''}${racksNote(wk.massT[i], src, tuning)}`
      scan.rowFlag[k] = coolant ? 1 : 0
    }
    return
  }
  scan.kind = SCAN_KIND.none
}

let gradeMults: readonly number[] | null = null

/** The `galaxy.json minerals.grades` index (poor … pristine) whose multiplier is nearest `mult`. */
function gradeIndexOf(mult: number): number {
  gradeMults ??= loadGalaxyConfig().minerals.grades.mult
  let best = 0
  for (let g = 1; g < gradeMults.length; g++) if (Math.abs(gradeMults[g] - mult) < Math.abs(gradeMults[best] - mult)) best = g
  return best
}

/**
 * The rock scan's field line (MC-a6, every Mk): "GRADE RICH · PLATINUM SIGNATURE". Empty outside every field (grade
 * 0); the signature part only when the rock's field holds the system's signature ore.
 */
function fieldLine(grade: number, signature: number, src: M2HudSource, tuning: M2HudTuning): string {
  if (!(grade > 0)) return ''
  const labels = tuning.labels
  const g = labels.grade.replace('{grade}', labels.grades[gradeIndexOf(grade)])
  if (signature < 0 || signature >= WARE_COUNT) return g
  return `${g} · ${labels.signature.replace('{ore}', src.space.data.wares.wares[signature].name.toUpperCase())}`
}

/**
 * The scan row note for an Intact part that carries a Mk (an installable type, economy plan SY-4b): " · MK II ·
 * INSTALLABLE". Empty for any other part.
 */
function installableNote(band: number, mk: number, tuning: M2HudTuning): string {
  if (band !== CONDITION_BAND.intact || mk < 1 || mk > 3) return ''
  return ` · ${tuning.labels.partMk[mk - 1]} · ${tuning.labels.installable}`
}

/**
 * The scan row note for a part heavier than the fitted racks take (`maxPartMassT`): " · NEEDS <the smallest cargo
 * module that takes it>", or " · TOO LARGE FOR HOLD" when none does; empty when the fitted racks take it.
 */
function racksNote(massT: number, src: M2HudSource, tuning: M2HudTuning): string {
  if (massT <= src.space.mirror.maxPartMassT) return ''
  for (const m of src.space.data.modules.modules) {
    if (m.kind === 'cargo' && m.stats.maxPartMassT >= massT) return ` · ${tuning.labels.needsRacks.replace('{module}', m.name.toUpperCase())}`
  }
  return ` · ${tuning.labels.tooLarge}`
}

// ---- Per-frame steps -------------------------------------------------------------------------------------------

/** Reads new space events: toasts and completed scans. */
function readEvents(state: M2HudState, src: M2HudSource, tuning: M2HudTuning): void {
  const ring = src.space.events
  if (state.ring !== ring) {
    // Another ring (the HUD just attached, or the space world was rebuilt): read it from its oldest event, like
    // the M1 HUD, so a notice raised before the first HUD frame still shows.
    state.ring = ring
    state.cursor = 0
  }
  const p = src.playerEid
  // A clearance that runs out stops the auto-dock in the same tick (docking.end): its cancel names that cause.
  let expired = false
  for (let i = firstReadableSpace(ring, state.cursor); i < ring.head; i++) {
    const s = spaceEventSlot(i)
    const type = ring.type[s]
    if (type === SEV.career) {
      careerToast(state, ring.a[s], ring.f[s], tuning)
    } else if (type === SEV.groupDefeated) {
      pushToast(state, ring.b[s] === ENCOUNTER_KIND.raiders ? 'info' : 'success', '', groupDefeatedText(ring.b[s], tuning))
    } else if (ring.eid[s] !== p) {
      continue
    } else if (type === SEV.scanComplete) {
      state.scan.dirty = true
    } else if (type === SEV.clearanceGranted) {
      // A moor grant (c = 1, M10 P1e2) has its own toast from the moorGranted that follows.
      if (ring.c[s] === 1) continue
      pushToast(state, 'success', stationSpeaker(src, ring.a[s], tuning), tuning.messages.clearanceGranted.replace('{seconds}', String(Math.round(ring.f[s]))))
    } else if (type === SEV.clearanceDenied) {
      pushToast(state, 'warning', stationSpeaker(src, ring.a[s], tuning), clearanceDeniedText(ring.b[s], tuning))
    } else if (type === SEV.moorGranted) {
      pushToast(state, 'success', stationSpeaker(src, ring.a[s], tuning), tuning.messages.moorGranted)
    } else if (type === SEV.moored) {
      pushToast(state, 'success', stationSpeaker(src, ring.a[s], tuning), tuning.messages.moored)
    } else if (type === SEV.clearanceExpired) {
      expired = true
      pushToast(state, 'warning', stationSpeaker(src, ring.a[s], tuning), tuning.messages.clearanceExpired)
    } else if (type === SEV.autodockStart) {
      pushToast(state, 'info', '', tuning.messages.autodockEngaged)
    } else if (type === SEV.autodockCancel) {
      pushToast(state, 'warning', '', autodockCancelText(src, expired, tuning))
    } else if (type === SEV.impact) {
      pushToast(state, 'error', '', tuning.messages.impact.replace('{amount}', String(Math.round(ring.f[s]))))
    } else if (type === SEV.holdFull) {
      pushToast(state, 'warning', '', ring.a[s] === HOLD_FULL_SOURCE.part ? tuning.messages.holdFull.part : tuning.messages.holdFull.chunk)
    }
  }
  state.cursor = ring.head
}

function ageToasts(state: M2HudState, dt: number, tuning: M2HudTuning): void {
  const life = tuning.toasts.seconds
  const fade = tuning.toasts.fadeSeconds
  const toasts = state.toasts
  for (let i = 0; i < toasts.length; i++) {
    const t = toasts[i]
    if (!t.active) continue
    t.age += dt
    if (t.age >= life) {
      t.active = false
      t.opacity = 0
      continue
    }
    const rest = life - t.age
    t.opacity = fade > 0 && rest < fade ? rest / fade : 1
  }
}

function updateStatus(state: M2HudState, src: M2HudSource, tuning: M2HudTuning): void {
  const mirror = src.space.mirror
  state.mode = src.space.player.mode
  state.cargoUsedT = mirror.cargoUsedT
  state.cargoCapacityT = mirror.cargoCapacityT
  state.cargoFull = mirror.cargoCapacityT > 0 && mirror.cargoUsedT >= mirror.cargoCapacityT - 1e-6
  state.collections = mirror.collections
  const career = src.career
  state.owned = mirror.owned || (career !== null && career.lease.owned)
  state.wallet = career !== null ? career.wallet : 0
  if (career !== null && !state.owned) {
    const left = career.lease.nextDueAt - mirror.gameTime
    state.leaseLeft = left > 0 ? left : 0
    state.leaseWarn = state.leaseLeft < tuning.leaseWarnSeconds
  } else {
    state.leaseLeft = 0
    state.leaseWarn = false
  }
}

/** World centre and bracket radius of the nav target into `navPos`; returns the radius, or -1 when it is gone. */
function navTargetPosition(src: M2HudSource, kind: number, index: number): number {
  const space = src.space
  if (kind === NAV.rock || kind === NAV.fragment) {
    if (!space.rocks.position(kind, index, navPos)) return -1
    return space.rocks.radius(kind, index)
  }
  if (kind === NAV.wreck) {
    const w = space.salvage.wrecks
    if (index < 0 || index >= w.capacity || w.alive[index] !== 1) return -1
    navPos.x = w.x[index]
    navPos.y = w.y[index]
    navPos.z = w.z[index]
    return w.radius[index]
  }
  if (kind === NAV.part) {
    const p = space.salvage.parts
    if (index < 0 || index >= p.capacity || p.alive[index] !== 1) return -1
    navPos.x = p.x[index]
    navPos.y = p.y[index]
    navPos.z = p.z[index]
    return 0
  }
  if (kind === NAV.station) {
    const list = space.stations.list
    if (index < 0 || index >= list.length) return -1
    const st = list[index]
    navPos.x = st.pos.x
    navPos.y = st.pos.y
    navPos.z = st.pos.z
    return st.radius
  }
  return -1
}

/** Scratch cruise target for `targetDropM` (a station: its drop counts off the hull). */
const cruiseTarget = createCruiseTarget()

/**
 * Seconds of cruise to a station `centreM` metres away with hull radius `radiusM` (CL-8, audit confusing 7): the
 * cruise ETA model (`cruiseEtaS` at full throttle, from the hull to the station's auto-drop, `targetDropM`) plus
 * `leadS` (the drive charge and spin-up). 0 within the mass-lock range off the hull (`massLock.stationM`, where the
 * drive can't engage) or the drop range (a cruise would drop at once), and with no travel tuning. Allocation-free.
 */
export function stationCruiseS(centreM: number, radiusM: number, travel: TravelConfig | null, leadS: number): number {
  if (travel === null) return 0
  const hull = centreM - radiusM
  if (!(hull > travel.massLock.stationM)) return 0
  cruiseTarget.station = 0
  cruiseTarget.radius = radiusM
  const eta = cruiseEtaS(hull, 1, travel, targetDropM(cruiseTarget, travel))
  if (!(eta > 0) || !Number.isFinite(eta)) return 0
  return eta + (leadS > 0 ? leadS : 0)
}

function updateNav(state: M2HudState, src: M2HudSource, tuning: M2HudTuning): void {
  const nav = state.nav
  const target = src.space.player.nav
  const radius = target.kind === NAV.none ? -1 : navTargetPosition(src, target.kind, target.index)
  if (radius < 0) {
    nav.kind = NAV.none
    return
  }
  nav.kind = target.kind
  nav.distance = dist(src, navPos.x, navPos.y, navPos.z)
  nav.cruiseS = target.kind === NAV.station ? stationCruiseS(nav.distance, radius, src.travel, src.cruiseLeadS) : 0
  projectWorld(src.cam, navPos.x, navPos.y, navPos.z, tuning.edgeMarginPx, nav.point)
  const b = tuning.bracket
  nav.halfPx = clampPx(radius * nav.point.pxPerMeter + b.padPx, b.minHalfPx, b.maxHalfPx)
}

function updateStations(state: M2HudState, src: M2HudSource, tuning: M2HudTuning): void {
  const list = src.space.stations.list
  const readout = src.space.docking.readout
  const docked = readout.phase === 'docked'
  const career = src.career
  const n = list.length < state.stations.length ? list.length : state.stations.length
  for (let i = 0; i < n; i++) {
    const m = state.stations[i]
    const st = list[i]
    m.name = st.def.name
    m.on = !docked
    let code: number = STATION_STATE.none
    if (readout.station === i) {
      if (readout.phase === 'cleared') code = STATION_STATE.cleared
      else if (readout.phase === 'autodock') code = STATION_STATE.autodock
      else if (docked) code = STATION_STATE.docked
    }
    m.state = code
    m.contract = false
    if (career !== null) {
      const active = career.contracts.active
      for (let c = 0; c < active.length; c++) {
        const k = active[c]
        if ((k.type === 'delivery' && k.station === st.def.id) || (k.type === 'courier' && k.params.destination === st.def.id)) m.contract = true
      }
    }
    if (!m.on) continue
    m.distance = dist(src, st.pos.x, st.pos.y, st.pos.z)
    m.cruiseS = stationCruiseS(m.distance, st.radius, src.travel, src.cruiseLeadS)
    projectWorld(src.cam, st.pos.x, st.pos.y, st.pos.z, tuning.edgeMarginPx, m.point)
  }
  for (let i = n; i < state.stations.length; i++) state.stations[i].on = false
}

function contractTypeIndex(type: string): number {
  for (let i = 0; i < CONTRACT_TYPES.length; i++) if (CONTRACT_TYPES[i] === type) return i
  return 0
}

function updateContracts(state: M2HudState, src: M2HudSource, tuning: M2HudTuning): void {
  const markers = state.contracts
  let n = 0
  const career = src.career
  if (career !== null) {
    const active = career.contracts.active
    for (let c = 0; c < active.length && n < markers.length; c++) {
      const k = active[c]
      const at = k.params.marker
      if (at === undefined || k.status !== 'active') continue
      const m = markers[n++]
      m.on = true
      m.type = contractTypeIndex(k.type)
      m.distance = dist(src, at[0], at[1], at[2])
      projectWorld(src.cam, at[0], at[1], at[2], tuning.edgeMarginPx, m.point)
    }
  }
  state.contractCount = n
  for (let i = n; i < markers.length; i++) markers[i].on = false
}

function updateScan(state: M2HudState, src: M2HudSource, tuning: M2HudTuning): void {
  const scan = state.scan
  const player = src.space.player
  const p = player.scanProgress
  scan.progress = clamp01(p)
  scan.scanning = p > 0 && p < 1
  const scanned = player.scanned
  if (scanned.kind === NAV.none) {
    scan.kind = SCAN_KIND.none
    scan.builtKind = NAV.none
    scan.builtIndex = -1
    return
  }
  if (scan.dirty || scanned.kind !== scan.builtKind || scanned.index !== scan.builtIndex) buildScanText(state, src, tuning)
}

function updateMining(state: M2HudState, src: M2HudSource): void {
  const m = state.mining
  const r = src.space.rocks.readout
  const f = r.fracture
  m.on = state.mode === TOOL.mining
  m.beamOn = r.beamOn
  m.contact = r.contact
  m.mode = r.mode
  m.power = clamp01(r.power)
  m.maxPower = clamp01(r.maxPower)
  m.heat = clamp01(r.heat)
  m.overheated = r.overheated
  m.cooldownLeft = r.cooldownLeft
  m.tooHard = f.tooHard
  m.fracture = f.active || f.tooHard
  m.progress = clamp01(f.progress)
  m.instability = clamp01(f.instability)
  const half = f.windowWidth * 0.5
  const lo = clamp01(f.windowCenter - half)
  const hi = clamp01(f.windowCenter + half)
  m.windowLeft = lo
  m.windowWidth = hi - lo
  m.band = r.power < f.windowCenter - half ? -1 : r.power > f.windowCenter + half ? 1 : 0
}

function scanRowOf(src: M2HudSource, wreck: number, part: number): number {
  const wk = src.space.tools.scan.wreck
  if (!wk.valid || wk.wreck !== wreck) return -1
  for (let r = 0; r < wk.partCount; r++) if (wk.part[r] === part) return r
  return -1
}

function updateSalvage(state: M2HudState, src: M2HudSource, tuning: M2HudTuning): void {
  const s = state.salvage
  const readout = src.space.salvage.readout
  const wrecks = src.space.salvage.wrecks
  const parts = src.space.salvage.parts
  s.on = state.mode === TOOL.salvage
  s.beamOn = readout.beamOn
  s.cutProgress = clamp01(readout.cutProgress)
  s.spilling = readout.spilling
  s.tractorOn = readout.tractorOn
  s.status = readout.status
  s.anchorCount = 0
  s.partCount = 0
  let focus = readout.wreck
  const nav = src.space.player.nav
  if (focus < 0 && nav.kind === NAV.wreck) focus = nav.index
  if (!s.on || focus < 0 || focus >= wrecks.capacity || wrecks.alive[focus] !== 1) {
    s.wreck = -1
    s.scanned = false
    return
  }
  s.wreck = focus
  const scan = src.space.tools.scan.wreck
  s.scanned = scan.valid && scan.wreck === focus
  const pb = tuning.partBracket
  for (let i = 0; i < parts.high; i++) {
    if (parts.alive[i] !== 1 || parts.secured[i] === 1 || parts.wreck[i] !== focus) continue
    const freed = parts.freed[i] === 1
    // Anchors of attached parts: projected rings with cut progress; coolant marked once scanned.
    if (!freed) {
      const count = parts.anchorCount[i]
      const base = i * MAX_ANCHORS_PER_PART
      for (let a = 0; a < count && s.anchorCount < ANCHOR_MARKERS; a++) {
        const j = base + a
        if (!projectWorld(src.cam, parts.ax[j], parts.ay[j], parts.az[j], 0, anchorPoint)) continue
        const k = s.anchorCount++
        s.anchorX[k] = anchorPoint.x
        s.anchorY[k] = anchorPoint.y
        s.anchorCut[k] = clamp01(parts.cut[j])
        s.anchorCoolant[k] = s.scanned && parts.coolant[j] === 1 ? 1 : 0
        s.anchorActive[k] = readout.part === i && readout.anchor === a ? 1 : 0
      }
    }
    if (s.partCount >= PART_BRACKETS) continue
    if (!projectWorld(src.cam, parts.x[i], parts.y[i], parts.z[i], 0, partPoint)) continue
    const k = s.partCount++
    s.partX[k] = partPoint.x
    s.partY[k] = partPoint.y
    s.partHalf[k] = clampPx(pb.radiusM * partPoint.pxPerMeter, pb.minHalfPx, pb.maxHalfPx)
    s.partCondition[k] = clamp01(parts.condition[i])
    s.partState[k] = readout.tractorPart === i ? 2 : freed ? 1 : 0
    const row = s.scanned ? scanRowOf(src, focus, i) : -1
    s.partKnown[k] = row >= 0 ? 1 : 0
    s.partValueLow[k] = row >= 0 ? scan.valueLow[row] : 0
    s.partValueHigh[k] = row >= 0 ? scan.valueHigh[row] : 0
  }
}

function dockPhaseCode(phase: string): number {
  if (phase === 'cleared') return DOCK_PHASE.cleared
  if (phase === 'autodock') return DOCK_PHASE.autodock
  if (phase === 'docked') return DOCK_PHASE.docked
  return DOCK_PHASE.space
}

function updateDock(state: M2HudState, src: M2HudSource): void {
  const d = state.dock
  const r = src.space.docking.readout
  d.phase = dockPhaseCode(r.phase)
  d.station = r.station
  d.clearanceLeft = r.clearanceLeft > 0 ? r.clearanceLeft : 0
  d.speed = r.speed
  d.speedLimit = r.speedLimit
  d.overLimit = r.speedLimit > 0 && r.speed > r.speedLimit
  d.inCorridor = r.inCorridor
  d.inBay = r.inBay
  d.alignDeg = r.alignDeg
  d.rollDeg = r.rollDeg
  const maxDeg = src.space.data.docking.dock.maxAlign * RAD_TO_DEG
  d.aligned = r.alignDeg <= maxDeg && (r.rollDeg < 0 ? -r.rollDeg : r.rollDeg) <= maxDeg
  d.padDistance = r.padDistance
  d.holdProgress = clamp01(r.holdProgress)
  d.on = d.phase === DOCK_PHASE.cleared || d.phase === DOCK_PHASE.autodock || r.inCorridor || r.inBay
  d.guides = d.phase !== DOCK_PHASE.docked && (r.inCorridor || r.inBay || d.phase === DOCK_PHASE.autodock)
  d.padOn = false
  d.noseOn = false
  const list = src.space.stations.list
  if (!d.guides || r.station < 0 || r.station >= list.length) return
  const bay = list[r.station].bay
  if (bay === null) return
  d.padOn = projectWorld(src.cam, bay.pad.x, bay.pad.y, bay.pad.z, 0, d.pad)
  // The nose cue: where the nose must point (the bay's inward axis, a direction at infinity).
  d.noseOn = projectDirection(src.cam, -bay.out.x, -bay.out.y, -bay.out.z, 0, d.nose)
}

/**
 * The dock prompt (CL-2, audit confusing 5): while cleared, "F AUTO-DOCK" with the station's hull within
 * `docking.autodockRange` (the range `tools.pressDock` auto-docks within), else "CLOSE TO 2.0 km TO AUTO-DOCK".
 * The key glyph follows the `dock` binding and the active device. Allocation-free: the lines are rebuilt only
 * when an input changes.
 */
function updateDockPrompt(state: M2HudState, src: M2HudSource, tuning: M2HudTuning): void {
  const d = state.dock
  const list = src.space.stations.list
  if (d.phase !== DOCK_PHASE.cleared || d.station < 0 || d.station >= list.length) {
    d.prompt = DOCK_PROMPT.none
    d.promptText = ''
    return
  }
  const st = list[d.station]
  const range = src.space.data.docking.autodockRange
  const c = state.promptCache
  const L = tuning.labels
  if (dist(src, st.pos.x, st.pos.y, st.pos.z) - st.radius <= range) {
    if (c.keyCode !== src.dockKey || c.pad !== src.padActive || c.keyTemplate !== L.dockPrompt || c.padGlyph !== L.dockPadGlyph) {
      c.keyCode = src.dockKey
      c.pad = src.padActive
      c.keyTemplate = L.dockPrompt
      c.padGlyph = L.dockPadGlyph
      c.keyText = dockKeyText(c)
    }
    d.prompt = DOCK_PROMPT.inRange
    d.promptText = c.keyText
    return
  }
  if (c.range !== range || c.farTemplate !== L.dockPromptFar) {
    c.range = range
    c.farTemplate = L.dockPromptFar
    c.farText = dockFarText(c)
  }
  d.prompt = DOCK_PROMPT.far
  d.promptText = c.farText
}

/** Recomputes `state` for this frame. Allocation-free (strings only on events). */
export function updateM2HudModel(state: M2HudState, src: M2HudSource, tuning: M2HudTuning): void {
  if (!src.paused) ageToasts(state, src.dt, tuning)
  readEvents(state, src, tuning)
  state.uiOpen = src.uiOpen
  state.alive = src.alive
  state.visible = src.career !== null && !src.uiOpen
  updateStatus(state, src, tuning)
  updateScan(state, src, tuning)
  updateDock(state, src)
  updateDockPrompt(state, src, tuning)
  updateMining(state, src)
  if (!state.visible) {
    state.nav.kind = NAV.none
    state.salvage.anchorCount = 0
    state.salvage.partCount = 0
    return
  }
  updateNav(state, src, tuning)
  updateStations(state, src, tuning)
  updateContracts(state, src, tuning)
  layoutMarkers(state, src, tuning)
  updateSalvage(state, src, tuning)
}

const reachHud = loadReachHudConfig()
const targetPoint = createScreenPoint()

/** Station + contract marker kinds for the declutter priority: stations outrank contract markers. */
const KIND_STATION = 'station'
const KIND_CONTRACT = 'poi'

/**
 * Lays out the station and contract markers (W1 polish): edge-pinned ones stay inside the HUD safe rect, clear of
 * the panels and the combat target's edge arrow, spread apart, and decluttered. Allocation-free.
 */
function layoutMarkers(state: M2HudState, src: M2HudSource, tuning: M2HudTuning): void {
  const l = state.edgeLayout
  const max = l.x.length
  let n = 0
  for (let i = 0; i < state.stations.length && n < max; i++) {
    const m = state.stations[i]
    if (!m.on) continue
    fillEdgeInput(l, n++, m, KIND_STATION, m.name)
  }
  for (let i = 0; i < state.contracts.length && n < max; i++) {
    const m = state.contracts[i]
    if (!m.on) continue
    fillEdgeInput(l, n++, m, KIND_CONTRACT, contractMarkerLabel(m.type, tuning))
  }
  l.count = n
  const rules = reachHud.edgeMarkers
  resetEdgeReserved(l)
  if (src.targetOn) {
    projectWorld(src.cam, src.tx, src.ty, src.tz, 0, targetPoint)
    // Only an off-screen target shows the edge arrow; the M2 margin matches the combat HUD's (hud.json).
    if (!projectWorld(src.cam, src.tx, src.ty, src.tz, tuning.edgeMarginPx, targetPoint)) reserveEdgeArrow(l, targetPoint.edgeX, targetPoint.edgeY, rules)
  }
  const nav = state.nav.point
  if (state.nav.kind !== NAV.none && !nav.onScreen) reserveEdgeArrow(l, nav.edgeX, nav.edgeY, rules)
  layoutEdgeMarkers(l, src.cam.width, src.cam.height, rules, reachHud.labels)
  n = 0
  for (let i = 0; i < state.stations.length && n < max; i++) {
    const m = state.stations[i]
    if (m.on) readEdgeOutput(l, n++, m)
  }
  for (let i = 0; i < state.contracts.length && n < max; i++) {
    const m = state.contracts[i]
    if (m.on) readEdgeOutput(l, n++, m)
  }
}

/** A contract marker's label (the tuning's fixed strings; no allocation). */
export function contractMarkerLabel(type: number, tuning: M2HudTuning): string {
  const t = tuning.labels.contractTypes
  const id = CONTRACT_TYPES[type]
  return id === 'recovery' ? t.recovery : id === 'clearRaiders' ? t.clearRaiders : id === 'courier' ? t.courier : t.delivery
}

function fillEdgeInput(l: EdgeMarkerLayout, k: number, m: M2Marker, kind: string, text: string): void {
  const p = m.point
  l.px[k] = p.x
  l.py[k] = p.y
  l.angle[k] = p.angle
  l.onScreen[k] = p.onScreen ? 1 : 0
  l.distance[k] = m.distance
  l.kind[k] = kind
  l.text[k] = text
}

function readEdgeOutput(l: EdgeMarkerLayout, k: number, m: M2Marker): void {
  m.sx = l.x[k]
  m.sy = l.y[k]
  m.edge = l.side[k] !== 0
  m.flip = l.flip[k] === 1
  m.full = l.full[k] === 1
}
